// ATO payment plans — the arithmetic behind the tracker. Pure functions, no React,
// no network, so they can be run and checked in Node. CommonJS (like vehicleWatch.js) so
// the daily alert function (ato-plan-alerts) works the plan with exactly this code.
// Keep it free of object/array spread and for…of: the web build turns those into
// imported helpers, which makes webpack treat the file as ESM and ignore module.exports.
//
// A plan is what the ATO agreed: a debt, a plan total (debt + the ATO's ESTIMATED
// general interest charge) and a dated instalment schedule. Payments are what was
// actually paid, each read off a bank receipt. Everything is summed in whole cents
// so nine instalments of $6,850 never drift off the plan total.
//
// The plan total is an estimate. GIC accrues daily on what is still owed, so paying
// early makes the real final figure SMALLER than the schedule says, and paying late
// makes it bigger. The ATO's own statement is the truth — the page says so, and a
// plan's total can be corrected to the ATO's figure at any time.

const { NSW_PUBLIC_HOLIDAYS } = require('./leaveRules');

const ENTITY_TYPES = ['Individual', 'Company', 'Trust', 'Partnership'];
const ACCOUNTS = ['Income tax account (ITA)', 'Integrated client account (ICA)', 'Activity statement', 'Other'];

// VLL's advice with both plans: pay two days before the due date "to avoid accidental
// defaults" — a BPAY made on the day can land the next business day and miss.
const PAY_EARLY_BUSINESS_DAYS = 2;

const cents = (n) => Math.round((Number(n) || 0) * 100);
const dollars = (c) => Math.round(c) / 100;

const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test((s || '').toString());
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Today on the LOCAL calendar — never toISOString(), which is UTC and reports
// yesterday until 10am in Sydney.
const todayIso = () => isoOf(new Date());

const isBusinessDay = (d) => d.getDay() !== 0 && d.getDay() !== 6 && !NSW_PUBLIC_HOLIDAYS.has(isoOf(d));

// The date to have paid by: `n` business days before the due date.
function payByDate(dueIso, n = PAY_EARLY_BUSINESS_DAYS) {
  if (!isIso(dueIso)) return '';
  const d = new Date(dueIso + 'T12:00:00');
  let left = n;
  while (left > 0) {
    d.setDate(d.getDate() - 1);
    if (isBusinessDay(d)) left--;
  }
  return isoOf(d);
}

const daysBetween = (aIso, bIso) =>
  Math.round((new Date(bIso + 'T12:00:00') - new Date(aIso + 'T12:00:00')) / 86400000);

// Live payments only — a soft-deleted payment is kept for Undo but never counted.
const livePayments = (payments, planId) =>
  (payments || []).filter((p) => !p.deletedAt && (!planId || p.planId === planId));

// Work a plan against its payments.
//
// Payments are applied to instalments OLDEST FIRST, in payment-date order, which is
// how the ATO treats a plan: an overpayment rolls forward into the next instalment,
// a short payment leaves the current one part-paid. Each instalment ends up:
//   paid       — covered, and the payment that finished it landed on or before the due date
//   late       — covered, but only after the due date (a default risk worth seeing)
//   part       — some paid, some still owing
//   overdue    — due date passed, nothing/part left owing
//   due        — the next one to pay, not yet late
//   upcoming   — later instalments
function workPlan(plan, payments, today = todayIso()) {
  const sched = (plan.instalments || []).slice()
    .filter((i) => isIso(i.date))
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((i) => ({ date: i.date, amountC: cents(i.amount), paidC: 0, coveredOn: '' }));
  const pays = livePayments(payments, plan.id)
    .filter((p) => isIso(p.date))
    .sort((a, b) => a.date.localeCompare(b.date) || (a.createdAt || '').localeCompare(b.createdAt || ''));

  const planTotalC = plan.planTotal != null && plan.planTotal !== ''
    ? cents(plan.planTotal)
    : sched.reduce((a, i) => a + i.amountC, 0);
  const scheduleTotalC = sched.reduce((a, i) => a + i.amountC, 0);
  const paidC = pays.reduce((a, p) => a + cents(p.amount), 0);

  // Allocate.
  let k = 0;
  pays.forEach((p) => {
    let left = cents(p.amount);
    while (left > 0 && k < sched.length) {
      const need = sched[k].amountC - sched[k].paidC;
      const take = Math.min(need, left);
      sched[k].paidC += take;
      left -= take;
      if (sched[k].paidC >= sched[k].amountC) { sched[k].coveredOn = p.date; k++; }
    }
  });

  let nextFound = false;
  const instalments = sched.map((i, idx) => {
    const owingC = i.amountC - i.paidC;
    let status;
    if (owingC <= 0) status = i.coveredOn > i.date ? 'late' : 'paid';
    else if (i.date < today) status = 'overdue';
    else if (!nextFound) { status = i.paidC > 0 ? 'part' : 'due'; }
    else status = i.paidC > 0 ? 'part' : 'upcoming';
    if (owingC > 0 && i.date >= today) nextFound = true;
    return {
      n: idx + 1,
      date: i.date,
      payBy: payByDate(i.date),
      amount: dollars(i.amountC),
      paid: dollars(i.paidC),
      owing: dollars(Math.max(0, owingC)),
      coveredOn: i.coveredOn,
      status,
    };
  });

  const dueToDateC = sched.filter((i) => i.date <= today).reduce((a, i) => a + i.amountC, 0);
  const aheadC = paidC - dueToDateC; // + ahead of schedule, − behind (in default territory)
  const remainingC = Math.max(0, planTotalC - paidC);
  const overdue = instalments.filter((i) => i.status === 'overdue');
  const next = instalments.find((i) => i.owing > 0 && i.date >= today) || null;
  const lastUnpaid = instalments.slice().reverse().find((i) => i.owing > 0) || null;

  return {
    planTotal: dollars(planTotalC),
    scheduleTotal: dollars(scheduleTotalC),
    scheduleMismatch: planTotalC !== scheduleTotalC, // the instalments don't add up to the stated total
    paid: dollars(paidC),
    remaining: dollars(remainingC),
    overpaid: dollars(Math.max(0, paidC - planTotalC)),
    pctPaid: planTotalC > 0 ? Math.min(100, Math.round((paidC / planTotalC) * 1000) / 10) : 0,
    dueToDate: dollars(dueToDateC),
    ahead: dollars(aheadC),
    overdue,
    overdueAmount: dollars(overdue.reduce((a, i) => a + cents(i.owing), 0)),
    next,
    finishDate: remainingC <= 0 ? (pays.length ? pays[pays.length - 1].date : '') : (lastUnpaid ? lastUnpaid.date : ''),
    instalmentsLeft: instalments.filter((i) => i.owing > 0).length,
    instalments,
    payments: pays,
    done: remainingC <= 0 && planTotalC > 0,
  };
}

// What a new payment does to a plan — for the "balance remaining" message after save.
function afterPayment(plan, payments, payment, today = todayIso()) {
  const before = workPlan(plan, payments, today);
  const after = workPlan(plan, livePayments(payments).concat([Object.assign({}, payment, { planId: plan.id })]), today);
  return { before, after };
}

// Suggest which plan a receipt belongs to. The receipt's BPAY/PRN reference is the
// strongest signal (every ATO client has their own payment reference number); failing
// that, a name on the receipt that matches the entity; failing that, the one plan whose
// next instalment equals the amount paid.
function suggestPlan(plans, extracted) {
  const live = (plans || []).filter((p) => !p.archived);
  if (!extracted || !live.length) return null;
  const digits = (s) => (s || '').toString().replace(/\D/g, '');
  const ref = digits(extracted.reference);
  if (ref.length >= 6) {
    const hit = live.find((p) => digits(p.prn) && (digits(p.prn) === ref || ref.includes(digits(p.prn))));
    if (hit) return { planId: hit.id, why: 'payment reference (PRN) matches' };
  }
  const text = `${extracted.payer || ''} ${extracted.description || ''} ${extracted.reference || ''}`.toLowerCase();
  const byName = live.filter((p) => {
    const first = (p.entity || '').toLowerCase().split(/\s+/)[0];
    return first && first.length > 2 && text.includes(first);
  });
  if (byName.length === 1) return { planId: byName[0].id, why: 'name on the receipt' };
  const amt = cents(extracted.amount);
  if (amt > 0) {
    const byAmt = live.filter((p) => (p.instalments || []).some((i) => cents(i.amount) === amt));
    if (byAmt.length === 1) return { planId: byAmt[0].id, why: 'amount matches an instalment' };
  }
  return null;
}

// A plan read off an ATO "Payment plans" print → the shape the page stores.
function planFromExtract(x) {
  const inst = (x.instalments || [])
    .map((i) => ({ date: isIso(i.date) ? i.date : '', amount: Number(i.amount) || 0 }))
    .filter((i) => i.date && i.amount > 0);
  return {
    entity: (x.client || '').toString(),
    account: (x.account || '').toString(),
    debt: Number(x.debt) || '',
    planTotal: Number(x.planTotal) || '',
    created: isIso(x.created) ? x.created : '',
    frequency: (x.frequency || 'Monthly').toString(),
    instalments: inst,
  };
}

// "LAST, First" (the ATO's style) → "First Last".
function tidyName(s) {
  const t = (s || '').toString().trim();
  const m = t.match(/^([^,]+),\s*(.+)$/);
  const flip = m ? `${m[2]} ${m[1]}` : t;
  return flip.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

// ── Reminders ────────────────────────────────────────────────────────────────
// Which reminder an unpaid instalment is at today. Only the CURRENT stage is ever sent,
// so a plan added late doesn't fire a backlog of stale "a week to go" pushes.
//   week     — within 7 days of the pay-by date
//   payby    — pay-by day (two business days before due) up to the day before due
//   due      — the due date itself, still unpaid
//   overdue  — after the due date, still unpaid — repeats DAILY: the ATO can end the plan
const REMIND_DAYS_BEFORE_PAYBY = 7;
function reminderStage(inst, today) {
  if (!inst || !(inst.owing > 0)) return '';
  if (today > inst.date) return 'overdue';
  if (today === inst.date) return 'due';
  if (today >= inst.payBy) return 'payby';
  if (daysBetween(today, inst.payBy) <= REMIND_DAYS_BEFORE_PAYBY) return 'week';
  return '';
}

// Everything to tell Paul today, given what has already been sent.
// `sent` is { "<planId>|<dueDate>": { week: iso, payby: iso, due: iso, overdue: iso } }.
// Returns { items, sent } — `sent` updated as if the push went out.
function dueReminders(plans, payments, sent, today) {
  const next = JSON.parse(JSON.stringify(sent || {}));
  const items = [];
  (plans || []).filter((p) => !p.archived).forEach((plan) => {
    const w = workPlan(plan, payments, today);
    w.instalments.forEach((inst) => {
      const stage = reminderStage(inst, today);
      if (!stage) return;
      const key = `${plan.id}|${inst.date}`;
      const done = next[key] || {};
      // once per stage; overdue once per DAY
      if (stage === 'overdue' ? done.overdue === today : !!done[stage]) return;
      done[stage] = today;
      next[key] = done;
      items.push({ planId: plan.id, entity: plan.entity, prn: plan.prn || '', stage, date: inst.date, payBy: inst.payBy, owing: inst.owing, remaining: w.remaining });
    });
  });
  return { items, sent: next };
}

// One push for the lot — Paul's and Holly's instalments share dates, so one alert.
function reminderMessage(items, fmtDay) {
  if (!items.length) return null;
  const money = (n) => (Number(n) || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
  const total = dollars(items.reduce((a, i) => a + cents(i.owing), 0));
  const worst = ['overdue', 'due', 'payby', 'week', 'reminder'].find((s) => items.some((i) => i.stage === s));
  // Tax-paid items (a BAS, a notice) carry `short`; then it's not just a plan instalment.
  const what = items.some((i) => i.short) ? 'ATO payment' : 'ATO instalment';
  const title = worst === 'overdue' ? `${what} OVERDUE — ${money(total)}`
    : worst === 'due' ? `${what} due TODAY — ${money(total)}`
    : worst === 'payby' ? `Pay the ATO today — ${money(total)}`
    : worst === 'reminder' ? `Reminder: ATO payment — ${money(total)}`
    : `${what} coming up — ${money(total)}`;
  // `note` = what Paul wrote on a reminder he set himself (the bell on Tax paid).
  const line = (i) => {
    const when = i.stage === 'overdue' ? `was due ${fmtDay(i.date)}`
      : i.stage === 'due' ? 'due today'
      : i.date ? `due ${fmtDay(i.date)}, pay by ${fmtDay(i.payBy)}`
      : 'no due date';
    return `${i.short || i.entity.split(' ')[0]} ${money(i.owing)} — ${when}${i.note ? ` (${i.note})` : ''}`;
  };
  return { title, body: items.map(line).join('\n') + '\nBPAY 75556 · ref = PRN', total, worst };
}

module.exports = { REMIND_DAYS_BEFORE_PAYBY, reminderStage, dueReminders, reminderMessage, ENTITY_TYPES, ACCOUNTS, PAY_EARLY_BUSINESS_DAYS, cents, dollars, todayIso, payByDate, daysBetween, livePayments, workPlan, afterPayment, suggestPlan, planFromExtract, tidyName };
